import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Link2, Barcode, Type, ImageIcon, FileText, Sparkles, Loader2, Check, AlertTriangle } from "lucide-react";
import { CameraButtons } from "../../components/CameraButtons";

const TABS = [
  { key: "link", label: "Produktlink", icon: Link2, placeholder: "https://shop.de/produkt…" },
  { key: "ean", label: "EAN / Barcode / Modellnr.", icon: Barcode, placeholder: "0195949032712 oder A3101…" },
  { key: "text", label: "Text / Produktname", icon: Type, placeholder: "z. B. Apple iPhone 15 Pro 256GB Titan Natur, Akku 91%…" },
  { key: "foto", label: "Foto", icon: ImageIcon },
  { key: "pdf", label: "Dokument / Rechnung", icon: FileText },
];

const STEPS = ["Eingabe analysieren", "Produkt erkennen", "Daten extrahieren", "Bestand durchsuchen", "Prüfung bereit"];

export default function KiImport() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("link");
  const [value, setValue] = useState("");
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/ki/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, base64: reader.result, mime: f.type });
    reader.readAsDataURL(f);
  };

  const analyze = async () => {
    setRunning(true); setResult(null); setStep(0);
    STEPS.forEach((_, i) => setTimeout(() => setStep(i + 1), 600 * (i + 1)));
    try {
      const { data } = await api.post("/ki/analyze", {
        input_type: tab, value, file_base64: file?.base64, file_mime: file?.mime,
      });
      setResult(data);
      const p = data.extracted?.product || {};
      setEdit({
        name: p.name || "", brand: p.brand || "", model: p.model || "", ean: p.ean || "",
        category_name: p.category || "", description: p.description || "",
        purchase_price: "", sale_price: "", condition: "", serial: "",
        storage: p.storage || "", color: p.color || "",
      });
      setStep(STEPS.length);
    } catch (e) {
      toast.error(apiError(e, "Analyse fehlgeschlagen"));
    }
    setRunning(false);
    api.get("/ki/usage").then((r) => setUsage(r.data)).catch(() => {});
  };

  const createArticle = async () => {
    setSaving(true);
    try {
      let categoryId = null;
      const cat = result.categories.find((c) => c.name.toLowerCase() === (edit.category_name || "").toLowerCase()) || result.categories[0];
      categoryId = cat?.id || null;
      const { data: pr } = await api.post("/products", {
        name: edit.name, brand: edit.brand, model: edit.model, ean: edit.ean,
        category_id: categoryId, category_name: edit.category_name, description: edit.description,
        specs: { storage: edit.storage, color: edit.color },
        source: tab === "link" ? { type: "link", url: value, imported_at: new Date().toISOString(), imported_by: "KI-Import" } : { type: tab },
      });
      await api.post("/articles", {
        product_id: pr.product.id, purchase_price: edit.purchase_price, sale_price: edit.sale_price,
        condition: edit.condition, serial: edit.serial, data: { storage: edit.storage, color: edit.color },
      });
      if (result.import?.id) await api.post(`/ki/imports/${result.import.id}/confirm`, edit).catch(() => {});
      toast.success("Produkt und Artikel wurden erstellt und liegen im Bestand.");
      navigate("/app/bestand");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8" data-testid="ki-import-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">KI Import</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Produkt per KI importieren</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">Link, EAN, Foto, Text oder Dokument – die KI erkennt die Produktart automatisch und lädt das passende Modul. Sie prüfen, bevor gespeichert wird.</p>
        </div>
        {usage && (
          <div className="text-xs font-mono text-zinc-500 border border-zinc-200 rounded-lg px-3 py-2 bg-white" data-testid="ki-usage-badge">
            KI-Imports: {usage.used}{usage.limit !== -1 ? ` / ${usage.limit}` : ""} ({usage.month})
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap gap-2 mb-5">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => { setTab(t.key); setResult(null); setFile(null); }}
                data-testid={`ki-tab-${t.key}`}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border transition-colors ${
                  tab === t.key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {(tab === "link" || tab === "ean") && (
            <div>
              <Label>{tab === "link" ? "Produkt-URL" : "EAN / Barcode / Modellnummer / IMEI"}</Label>
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={TABS.find((t) => t.key === tab).placeholder}
                data-testid="ki-value-input" className="mt-1.5 font-mono text-sm" />
            </div>
          )}
          {tab === "text" && (
            <div>
              <Label>Produkttext</Label>
              <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={5} data-testid="ki-text-input"
                placeholder="Produktbeschreibung, Anzeigentext, Angebot…" className="mt-1.5" />
            </div>
          )}
          {(tab === "foto" || tab === "pdf") && (
            <div className="space-y-4">
              <div onClick={() => fileRef.current?.click()} data-testid="ki-file-drop"
                className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:bg-zinc-50 transition-colors">
                {file ? (
                  <div className="text-sm font-mono text-zinc-700">{file.name}</div>
                ) : (
                  <div className="text-sm text-zinc-400">{tab === "foto" ? "Foto auswählen (PNG/JPG)" : "PDF / Rechnung auswählen"}</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept={tab === "foto" ? "image/*" : "application/pdf"} className="hidden" onChange={onFile} data-testid="ki-file-input" />
              {tab === "foto" && (
                <CameraButtons onCapture={(imgs) => setFile({ name: "kamera-aufnahme.jpg", base64: imgs[0], mime: "image/jpeg" })} />
              )}
              {tab === "foto" && <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Zusatzinfo (optional)" data-testid="ki-foto-hint" />}
            </div>
          )}
          <Button onClick={analyze} disabled={running || (tab !== "foto" && tab !== "pdf" && !value.trim()) || ((tab === "foto" || tab === "pdf") && !file)}
            data-testid="ki-analyze-button" className="mt-5 bg-zinc-900 hover:bg-zinc-800 text-white w-full sm:w-auto">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {running ? "KI analysiert…" : "KI-Analyse starten"}
          </Button>

          {(running || step > 0) && !result && (
            <div className="mt-6 space-y-2" data-testid="ki-stepper">
              {STEPS.map((s, i) => (
                <div key={s} className={`flex items-center gap-3 text-sm font-mono transition-all ${
                  step > i ? "text-zinc-900" : step === i && running ? "text-zinc-900 step-active" : "text-zinc-300"}`}>
                  {step > i ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-current" />}
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {result?.multiple_products && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-testid="ki-multi-warning">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="text-sm text-amber-900">Mehrere mögliche Produkte im Bestand gefunden. Bitte prüfen Sie die Treffer – es wird nichts automatisch überschrieben.</div>
            </div>
          )}
          {result && edit && (
            <div className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm fade-up" data-testid="ki-result-card">
              <div className="flex items-center justify-between">
                <h3 className="font-display font-medium text-lg">Erkanntes Produkt – bitte prüfen</h3>
                <span className="text-[10px] font-mono bg-zinc-900 text-white px-2 py-1 rounded-full">
                  {Math.round((result.extracted?.product?.confidence || 0) * 100)} % sicher
                </span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="ki-edit-name" className="mt-1.5" /></div>
                <div><Label>Hersteller</Label><Input value={edit.brand} onChange={(e) => setEdit({ ...edit, brand: e.target.value })} data-testid="ki-edit-brand" className="mt-1.5" /></div>
                <div><Label>Modell</Label><Input value={edit.model} onChange={(e) => setEdit({ ...edit, model: e.target.value })} data-testid="ki-edit-model" className="mt-1.5" /></div>
                <div><Label>Kategorie</Label><Input value={edit.category_name} onChange={(e) => setEdit({ ...edit, category_name: e.target.value })} data-testid="ki-edit-category" className="mt-1.5" /></div>
                <div><Label>EAN</Label><Input value={edit.ean} onChange={(e) => setEdit({ ...edit, ean: e.target.value })} data-testid="ki-edit-ean" className="mt-1.5 font-mono" /></div>
                <div><Label>Speicher / Größe</Label><Input value={edit.storage} onChange={(e) => setEdit({ ...edit, storage: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Farbe</Label><Input value={edit.color} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Einkaufspreis (€)</Label><Input type="number" step="0.01" value={edit.purchase_price} onChange={(e) => setEdit({ ...edit, purchase_price: e.target.value })} data-testid="ki-edit-purchase-price" className="mt-1.5" /></div>
                <div><Label>Verkaufspreis (€)</Label><Input type="number" step="0.01" value={edit.sale_price} onChange={(e) => setEdit({ ...edit, sale_price: e.target.value })} data-testid="ki-edit-sale-price" className="mt-1.5" /></div>
                <div><Label>Zustand</Label><Input value={edit.condition} onChange={(e) => setEdit({ ...edit, condition: e.target.value })} placeholder="z. B. Sehr gut" data-testid="ki-edit-condition" className="mt-1.5" /></div>
                <div><Label>Seriennr. / IMEI</Label><Input value={edit.serial} onChange={(e) => setEdit({ ...edit, serial: e.target.value })} data-testid="ki-edit-serial" className="mt-1.5 font-mono" /></div>
                <div className="col-span-2"><Label>Beschreibung</Label><Textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={3} className="mt-1.5" /></div>
              </div>
              {result.customer_matches?.length > 0 && (
                <div className="mt-4 border border-zinc-200 rounded-lg p-3" data-testid="ki-customer-matches">
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2">
                    {result.multiple_customers ? "Mehrere mögliche Kunden gefunden" : "Möglicher bestehender Kunde gefunden"}
                  </div>
                  {result.customer_matches.map((c) => (
                    <div key={c.id} className="text-sm text-zinc-700 py-1">{c.first_name} {c.last_name} {c.company && `· ${c.company}`} <span className="font-mono text-xs text-zinc-400">{c.number}</span></div>
                  ))}
                </div>
              )}
              {result.product_matches?.length > 0 && (
                <div className="mt-4 border border-zinc-100 rounded-lg p-3 bg-zinc-50" data-testid="ki-product-matches">
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2">Vorhandene ähnliche Produkte</div>
                  {result.product_matches.map((p) => (
                    <div key={p.id} className="text-sm text-zinc-700 py-1 flex justify-between"><span>{p.name}</span><span className="font-mono text-xs text-zinc-400">{p.ean || p.brand}</span></div>
                  ))}
                </div>
              )}
              <Button onClick={createArticle} disabled={saving || !edit.name} data-testid="ki-create-article-button"
                className="mt-5 bg-zinc-900 hover:bg-zinc-800 text-white">
                {saving ? "Speichern…" : "Artikel erstellen & in Bestand legen"}
              </Button>
            </div>
          )}
          {!result && !running && (
            <div className="bg-white border border-dashed border-zinc-300 rounded-xl p-10 text-center text-sm text-zinc-400" data-testid="ki-empty-state">
              <Sparkles className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
              Starten Sie eine Analyse – das Ergebnis erscheint hier zur Prüfung, bevor etwas gespeichert wird.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
